"use client";

import { Plus } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const faqs = [{
  question: "What materials can you engrave?",
  answer: "We specialize in a wide range of industrial materials, including stainless steel, aluminum, brass, and high-performance phenolic resins. Our precision laser systems are calibrated to handle everything from delicate components to heavy-duty industrial plates with absolute accuracy."
}, {
  question: "Do you provide submittal packages?",
  answer: "Yes. We understand the requirements of large-scale construction and engineering projects. We provide comprehensive submittal packages, including material data sheets, layout proofs, and compliance documentation to ensure your project meets all specifications before production begins."
}, {
  question: "What is the turnaround time for custom orders?",
  answer: "Standard production typically takes 3 to 5 business days. For urgent requirements or large-scale rollouts, we offer expedited services to ensure your project timeline remains on track. We prioritize clear communication regarding delivery schedules."
}, {
  question: "Are your arc flash labels compliant?",
  answer: "Absolutely. All our arc flash labels are manufactured to meet current NFPA 70E and OSHA standards. We use industrial-grade, UV-resistant materials designed to withstand harsh environments, ensuring your safety signage remains legible and compliant for years."
}, {
  question: "How do I submit my design files?",
  answer: "We accept most vector formats, including .DXF, .DWG, .AI, and .EPS. If you only have a sketch or a PDF, our design team can digitize your requirements into production-ready files. We ensure every detail is captured before we start the engraving process."
}, {
  question: "What makes your marking process superior?",
  answer: "Our facility utilizes advanced fiber laser technology that creates permanent, high-contrast marks without compromising the structural integrity of the material. Unlike traditional stamping or printing, our marks are resistant to chemicals, heat, and abrasion."
}, {
  question: "Can you handle high-volume production?",
  answer: "Yes. Our automated laser systems are designed for both precision prototyping and high-volume industrial runs. Whether you need a single custom part or thousands of serialized tags, we maintain consistent quality across every piece."
}, {
  question: "Do you offer shipping services?",
  answer: "We provide secure, professional packaging and reliable shipping options for all orders. Whether you are local or across the country, we ensure your components arrive safely and on schedule, ready for immediate installation."
}];
export default function Faq() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="2463" id="faq" className="bg-background text-foreground py-24 md:py-32 lg:py-40 px-6 md:px-10 lg:px-16">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div ref={leftRef} className={`lg:col-span-5 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="lg:sticky lg:top-24">
              <div className="font-mono text-xs tracking-widest uppercase text-primary mb-8">
                PROJECT SUPPORT / 03
              </div>

              <h2 className="font-default font-bold text-foreground mb-10" style={{
              fontSize: "clamp(2.75rem, 5vw, 5rem)",
              lineHeight: "0.95",
              letterSpacing: "-0.03em"
            }}>
                Technical
                <br />
                Support
                <br />
                Answers.
              </h2>

              <p className="font-default text-base md:text-lg leading-relaxed mb-10" style={{
              maxWidth: "360px",
              color: "#A1A1AA"
            }}>
                We are here to assist with your industrial marking needs. Below are the most common questions we receive regarding our processes and capabilities.
              </p>

              <Link to="/ContactPage">
                <Button size="lg" className="font-default font-medium tracking-wider uppercase text-xs border-primary text-primary bg-transparent hover:bg-primary hover:text-primary-foreground transition-all duration-300 px-8 py-6 h-auto">
                  Contact Our Team
                </Button>
              </Link>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-1000 ease-out delay-200 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Accordion type="single" collapsible defaultValue="item-0" className="flex flex-col gap-3">
              {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border-0 backdrop-blur-sm transition-all duration-300 group" style={{
              backgroundColor: "var(--card)",
              border: "1px solid var(--border)",
              boxShadow: "var(--shadow-md)"
            }}>
                  <AccordionTrigger className="hover:no-underline px-6 md:px-8 py-6 md:py-7 min-h-[96px] gap-6 [&>svg]:hidden group-hover:[border-color:var(--primary)]">
                    <div className="flex items-center gap-6 md:gap-8 flex-1 text-left">
                      <span className="font-default font-bold text-xs text-primary shrink-0 tracking-wider">
                        Q.{String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="font-default font-medium text-base md:text-lg text-foreground leading-snug">
                        {faq.question}
                      </span>
                    </div>
                    <Plus className="size-6 text-primary shrink-0 transition-transform duration-300 group-data-[state=open]:rotate-45" strokeWidth={1.5} />
                  </AccordionTrigger>
                  <AccordionContent className="px-6 md:px-8 pb-8 pt-0">
                    <div className="pl-0 md:pl-[88px]">
                      <p className="font-default text-base md:text-[1.0625rem]" style={{
                    lineHeight: "1.75",
                    maxWidth: "600px",
                    color: "#A1A1AA"
                  }}>
                        {faq.answer}
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}